/**
 * Utility functions for the UploadWidget.
 * Contains extracted logic for Horizon widget validation/import and Swagger/OAS validation.
 * Uses browser-safe validation (no Node.js dependencies).
 *
 * @author horizon-team
 */
import { widgetSchema } from 'shared/widgets';

/** Protocols permitted for the Swagger spec source URL. */
const SAFE_SWAGGER_URL_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Validates that a Swagger/OAS spec URL is safe to hand to swagger-ui-react.
 *
 * swagger-ui-react fetches and renders whatever the `url` prop points to, so an
 * attacker-controlled value such as `javascript:`, `data:`, or `blob:` could be
 * used to inject script or smuggle markup. We therefore only allow absolute
 * `http(s)` URLs (S3 signed URLs are always https) and reject everything else.
 *
 * @param url - The candidate spec URL (typically an S3 signed URL).
 * @returns `true` only when the URL is an absolute http/https URL.
 */
export const isSafeSwaggerUrl = (url: string | undefined | null): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  try {
    const { protocol } = new URL(url);
    return SAFE_SWAGGER_URL_PROTOCOLS.has(protocol);
  } catch {
    // Relative or malformed URLs cannot be validated and are treated as unsafe.
    return false;
  }
};

/**
 * Fetches JSON content from a file URL (typically an S3 signed URL).
 *
 * @param fileUrl - The URL to fetch JSON from.
 * @returns The parsed JSON object.
 * @throws Error if the fetch or JSON parsing fails.
 */
export const fetchJsonFromUrl = async (fileUrl: string): Promise<unknown> => {
  const response = await fetch(fileUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Validates that a JSON object conforms to the Horizon widget schema.
 *
 * @param json - The JSON object to validate.
 * @returns An object indicating validity and the validated widget data (or validation errors).
 */
export const validateHorizonWidget = (
  json: unknown
): { valid: boolean; widget?: any; errors?: string[] } => {
  const Validator = require('jsonschema').Validator;
  const v = new Validator();
  const result = v.validate(json, widgetSchema);

  if (!result.valid || result.instance === undefined) {
    return {
      valid: false,
      errors: result.errors?.map((e: any) => e.message) ?? [
        'Invalid widget JSON',
      ],
    };
  }

  return { valid: true, widget: json };
};

/**
 * Validates that a JSON object conforms to the OpenAPI 3.0 specification.
 * Performs browser-safe structural validation without Node.js dependencies.
 * Only OpenAPI 3.0.x is supported (swagger-ui-react 4.x does not support 3.1+).
 *
 * Checks:
 * - Presence of "openapi" field matching 3.0.x
 * - Required top-level fields: info, paths
 * - info.title and info.version exist
 *
 * @param fileUrl - The S3 URL containing the Swagger/OAS JSON file.
 * @returns An object indicating validity, the parsed spec, and any errors.
 */
export const validateSwaggerSpec = async (
  fileUrl: string
): Promise<{ valid: boolean; spec?: any; errors?: string[] }> => {
  try {
    const json = await fetchJsonFromUrl(fileUrl);

    if (typeof json !== 'object' || json === null) {
      return {
        valid: false,
        errors: ['Uploaded file is not a valid JSON object'],
      };
    }

    const spec = json as Record<string, any>;
    const errors: string[] = [];

    // Only OpenAPI 3.0.x is supported
    const isOpenApi3_0 =
      typeof spec.openapi === 'string' && /^3\.0\.\d+$/.test(spec.openapi);
    if (!isOpenApi3_0) {
      errors.push(
        `Unsupported OpenAPI version: "${spec.openapi ??
          'missing'}". Only OpenAPI 3.0.x is supported (e.g. "3.0.0", "3.0.1", "3.0.3").`
      );
    }

    // Must have "info" object with title and version
    if (typeof spec.info !== 'object' || spec.info === null) {
      errors.push('Missing required field: "info"');
    } else {
      if (typeof spec.info.title !== 'string' || !spec.info.title.trim()) {
        errors.push('Missing required field: "info.title"');
      }
      if (typeof spec.info.version !== 'string' || !spec.info.version.trim()) {
        errors.push('Missing required field: "info.version"');
      }
    }

    // Must have "paths" object
    if (typeof spec.paths !== 'object' || spec.paths === null) {
      errors.push('Missing required field: "paths"');
    }

    if (errors.length > 0) {
      return { valid: false, errors };
    }

    return { valid: true, spec };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Invalid OpenAPI specification';
    return { valid: false, errors: [message] };
  }
};

/**
 * Determines helper text to display based on the current upload mode.
 *
 * @param uploadMode - The current upload mode ('horizon' or 'swagger').
 * @returns The helper text string describing accepted file content.
 */
export const getUploadHelperText = (
  uploadMode: 'horizon' | 'swagger'
): string => {
  return uploadMode === 'swagger'
    ? 'Accepted file type: JSON (OpenAPI 3.0 Specification)'
    : 'Accepted file type: JSON (Horizon Widget)';
};
